import { FormLabel, Button, Stack, Input, Select, IconButton } from '@chakra-ui/react';
import { ChangeEvent, useState } from 'react';
import { useLocalization } from '~/framework';
import { add, differenceInSeconds } from 'date-fns';
import { mapValues } from '@dozuki/web-js/utils';
import { Icon } from '~/ui-components';
import { faClock, faClose } from '@fortawesome/pro-regular-svg-icons';
import { CourseAssignmentOptions } from './CourseAssignmentOptions';

type DeadlineUnit = 'days' | 'weeks' | 'months' | 'years';

type Deadline = null | {
   unit: DeadlineUnit;
   quantity: number;
};

type AssignmentDeadlineInputProps = {
   assignment: CourseAssignmentOptions;
   onChange: (assignment: CourseAssignmentOptions) => void;
};

export function AssignmentDeadlineInput({ assignment, onChange }: AssignmentDeadlineInputProps) {
   const [deadline, setDeadline] = useState<Deadline>(null);

   const { _js } = useLocalization();

   const frequencyLabels: Record<DeadlineUnit, string> = {
      days: _js('Days'),
      weeks: _js('Weeks'),
      months: _js('Months'),
      years: _js('Years'),
   };

   function handleChange(value: Deadline) {
      if (value === null) {
         setDeadline(null);
         onChange({
            ...assignment,
            due_within: null,
         });
      } else {
         const { unit, quantity } = value;
         const dueWithin = Math.round(
            differenceInSeconds(add(Date.now(), { [unit]: quantity }).getTime(), Date.now())
         );

         setDeadline(value);
         onChange({
            ...assignment,
            due_within: dueWithin,
         });
      }
   }

   function addDeadline() {
      handleChange({
         quantity: 30,
         unit: 'days',
      });
   }

   function clearDeadline() {
      handleChange(null);
   }

   function handleQuantityChange(e: ChangeEvent<HTMLInputElement>) {
      let value = parseInt(e.target.value);

      if (isNaN(value)) {
         value = 0;
      }

      // Do not allow values larger than 1000
      if (value > 1000) {
         value = 1000;
      }

      // Do not allow values smaller than 0
      if (value < 0) {
         value = 0;
      }

      handleChange({
         // Deadline is guaranteed to not be null if it's quantity is changing
         ...(deadline as NonNullable<Deadline>),

         quantity: value,
      });
   }

   function handleUnitChange(e: ChangeEvent<HTMLSelectElement>) {
      handleChange({
         // Deadline is guaranteed to not be null if it's quantity is changing
         ...(deadline as NonNullable<Deadline>),

         unit: e.target.value as DeadlineUnit,
      });
   }

   return (
      <>
         <FormLabel>{_js('Deadline (optional)')}</FormLabel>
         {deadline === null ? (
            <Button onClick={addDeadline} rightIcon={<Icon type={faClock} />}>
               {_js('Add a Deadline')}
            </Button>
         ) : (
            <Stack direction="row">
               <Input
                  aria-label={_js('Number of %1', frequencyLabels[deadline.unit])}
                  type="number"
                  value={`${deadline.quantity}`}
                  onChange={handleQuantityChange}
               />
               <Select
                  aria-label={_js('Deadline Unit')}
                  value={deadline.unit}
                  onChange={handleUnitChange}
               >
                  {mapValues(frequencyLabels, (label, value) => (
                     <option value={value} key={value}>
                        {label}
                     </option>
                  ))}
               </Select>
               <IconButton aria-label={_js('Remove Deadline')} onClick={clearDeadline}>
                  <Icon type={faClose} />
               </IconButton>
            </Stack>
         )}
      </>
   );
}
